import React, { Fragment, useEffect } from "react";
import { styled } from '@mui/material/styles';
import { IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import EventIcon from "@mui/icons-material/Event";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { setStartYear, setEndYear } from "../../../actions/filters";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import HtmlTooltip from "./HtmlTooltip";

import InfoIcon from "@mui/icons-material/Info";

const PREFIX = 'Year';

const classes = {
  root: `${PREFIX}-root`,
  infoIcon: `${PREFIX}-infoIcon`,
  title: `${PREFIX}-title`,
  remove: `${PREFIX}-remove`,
  select: `${PREFIX}-select`,
  icon: `${PREFIX}-icon`,
  header: `${PREFIX}-header`,
  bold: `${PREFIX}-bold`,
};

const Root = styled('div')((
  {
    theme
  }
) => ({

  [`& .${classes.infoIcon}`]: {
    color: '#0078a9',
    fontSize: '0.9rem',
    margin: '0 0.25rem'
    },

  [`& .${classes.title}`]: {
    color: "#4c4c4c",
    fontSize: "0.9rem",
    margin: "0 0 0.5em 0",
    textTransform: "uppercase",
  },

  [`& .${classes.remove}`]: {
    background: "#999999",
    borderRadius: "50%",
    padding: "0em",
    color: "#fff",
    position: "absolute",
    right: "0.5em",
    top: "0.5em",
    "&:hover": {
      background: "#909090",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },

  [`& .${classes.select}`]: {
    margin: "1em",
    "& .MuiAutocomplete-popupIndicatorOpen": {
      transform: "rotate(0)",
    },
    "& .MuiAutocomplete-popper": {
      height: "20em",
    },
  },

  [`& .${classes.icon}`]: {
    transform: "rotate(180) !important",
  },

  [`& .${classes.header}`]: {
    display: "flex",
    textAlign: "center",
    width: "max-content",
    "&:hover": {
      cursor: "pointer",
    },
  },

  [`& .${classes.bold}`]: {
    fontWeight: "bold",
  },

}));

const Year = ({
  setStartYear,
  setEndYear,
  startYear,
  endYear,
  analysisType,
}) => {

  const TooltipText = () => {
    return (
      <Fragment>
        <h3>Historical Event Year</h3>
        <p>
          Limit historical events by the year they occurred. Modeled scenario
          events and locations will not be included if the filter is active.
        </p>
      </Fragment>
    );
  };

  const remove = () => {
    setStartYear(null);
    setEndYear(null);
  };

  useEffect(() => {
    if (analysisType.probabilistic || analysisType.deterministic) {
      setStartYear(null);
      setEndYear(null);
    }
  }, [analysisType]);

  const handleChange = (event, value, id) => {
    switch (id) {
      case "start":
        if (isNaN(parseInt(value))) {
          setStartYear(null);
          break;
        }
        if (
          parseInt(value) <= (endYear === null ? parseInt(value) + 1 : endYear)
        ) {
          setStartYear(parseInt(value));
          break;
        }
        alert("The start year cannot be greater than the end year");
        break;
      case "end":
        if (isNaN(parseInt(value))) {
          setEndYear(null);
          break;
        }
        if (
          parseInt(value) >=
          (startYear === null ? parseInt(value) - 1 : startYear)
        ) {
          setEndYear(parseInt(value));
          break;
        }
        alert("The end year cannot be less than the start year");
        break;
      default:
        console.error("unable to set date state");
    }
  };

  const today = new Date();
  const startYearBounds = 1970;
  const endYearBounds = today.getFullYear() + 1;
  const years = new Array(endYearBounds - startYearBounds)
    .fill()
    .map((x, i) => (i + startYearBounds).toString())
    .reverse();

  useEffect(() => {}, [startYear, endYear]);
  
return (
    <Root className={classes.root}
	sx={{
		border: "1px solid #e9e9e9",
	    borderRadius: "5px",
	    padding: "1em",
	    position: "relative",
	    margin: "1em 0",
	    background: "#fff",
	}}>
		<div>
	      <HtmlTooltip
	        disableFocusListener
	        disableTouchListener
	        title={<TooltipText />}
	        placement="top"
	      >
	        <div className={classes.header}>
	          <div className={classes.title}>Historical Event Year</div>
	          <InfoIcon className={classes.infoIcon} />
	        </div>
	      </HtmlTooltip>
	      <IconButton
	        className={classes.remove}
	        onClick={remove}
	        alt="clear"
	        aria-label="clear year filter"
	      >
	        <RemoveIcon />
	      </IconButton>
	      <Autocomplete
	        className={classes.select}
	        id="start"
	        size="small"
	        options={years}
	        getOptionLabel={(year) => year}
	        value={startYear ? `${startYear}` : ""}
	        renderInput={(params) => <TextField {...params} label="Start Year" />}
	        popupIcon={<EventIcon className={classes.icon} />}
	        onChange={(event, value, id) => handleChange(event, value, "start")}
	      />
	      <Autocomplete
	        className={classes.select}
	        id="end"
	        size="small"
	        options={years}
	        getOptionLabel={(year) => year}
	        value={endYear ? `${endYear}` : ""}
	        renderInput={(params) => (
	          <TextField {...params} label="End Year" id="end-textfield" />
	        )}
	        popupIcon={<EventIcon className={classes.icon} />}
	        onChange={(event, value, id) => handleChange(event, value, "end")}
	      />
		</div>
    </Root>
  );
};

Year.propTypes = {
  setStartYear: PropTypes.func.isRequired,
  setEndYear: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  startYear: state.filters.startYear,
  endYear: state.filters.endYear,
  analysisType: state.filters.analysisType,
});

export default connect(mapStateToProps, { setStartYear, setEndYear })(Year);
